import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// saas-ui-shared (PlatformServices) + app overrides under src/agenticsaas — auth & SaaS features
import { AuthProvider } from './agenticsaas/contexts/AuthContext';
import { OrganizationProvider } from './agenticsaas/contexts/OrganizationContext';
import { NotificationProvider } from './agenticsaas/contexts/NotificationContext';
import ProtectedRoute from './agenticsaas/components/auth/ProtectedRoute';
import PublicRoute from './agenticsaas/components/auth/PublicRoute';
import { EffectiveAccessProvider } from './agenticsaas/contexts/EffectiveAccessContext';
import ProtectedAppLayout from './components/layout/ProtectedAppLayout';
import LoginForm from './agenticsaas/components/auth/LoginForm';
import RegisterForm from './agenticsaas/components/auth/RegisterForm';
import BaaAcceptPage from './agenticsaas/components/auth/BaaAcceptPage';
import ForgotPasswordPage from './agenticsaas/components/auth/ForgotPasswordPage';
import ResetPasswordPage from './agenticsaas/components/auth/ResetPasswordPage';
import EmailVerifyPage from '@saas-foundation/shared-frontend/auth/EmailVerifyPage';
import InvitationAcceptPage from './pages/InvitationAcceptPage';
import OnboardingPage from './pages/OnboardingPage';
import { DEFAULT_HOME_ROUTE } from './config/navAccessMap';

import { getStripePublishableKey } from './utils/stripePublishableKey';
import { CrossAppNavHost } from './shared/crossAppNav';

const stripeKey = getStripePublishableKey();
// Avoid Stripe.js on arbitrary HTTP hosts unless HTTPS, explicit flag, or localhost (Stripe allows test keys on localhost HTTP).
const allowStripeOnHttp = process.env.REACT_APP_STRIPE_ALLOW_HTTP === 'true';
const isSecure =
  typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
const isLocalhost =
  typeof window !== 'undefined' &&
  window.location &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const stripePromise =
  stripeKey && (isSecure || allowStripeOnHttp || isLocalhost) ? loadStripe(stripeKey) : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Use cache if available - faster initial load
      refetchOnReconnect: false, // Don't refetch on reconnect to avoid unnecessary requests
      retry: 1,
      staleTime: 60000, // Consider data stale after 60 seconds (was 0)
      gcTime: 300000, // Cache for 5 minutes (renamed from cacheTime in v5) - allows instant display
    },
  },
});

// Make queryClient available globally for logout
if (typeof window !== 'undefined') {
  window.queryClient = queryClient;
}

function App() {
  // Suppress console errors for Stripe wallet-config 401 errors
  // and unhandled promise rejections for authentication errors
  useEffect(() => {
    // Intercept console.error to suppress Stripe wallet-config errors
    const originalConsoleError = console.error;
    console.error = function(...args) {
      const message = args.join(' ');
      // Suppress Stripe wallet-config 401 errors (merchant account may not have wallet features enabled)
      if (message.includes('merchant-ui-api.stripe.com/elements/wallet-config') ||
          (message.includes('401') && message.includes('stripe'))) {
        // Suppress silently or log at debug level
        console.debug('[Stripe] Wallet features not available (merchant account may not have wallet features enabled)');
        return;
      }
      // Suppress Agentic Platform connection refused errors (service may not be running)
      if ((message.includes('ERR_CONNECTION_REFUSED') || message.includes('Connection refused')) &&
          (message.includes(':8076') || message.includes('agentic-platform'))) {
        console.debug('[Agentic Platform] Service not available (connection refused - service may not be running)');
        return;
      }
      // Task Manager / Activity Log — prefer shell /rcm-api proxies; suppress direct-port noise
      if (
        (message.includes('ERR_CONNECTION_REFUSED') ||
          message.includes('Connection refused') ||
          message.includes('ERR_CONNECTION_TIMED_OUT') ||
          message.includes('Failed to fetch') ||
          message.includes('ActivityLog')) &&
        (message.includes(':8051') ||
          message.includes(':8050') ||
          message.includes('/api/v1/workqueue') ||
          message.includes('activity-events') ||
          message.includes('/rcm-api/task-manager') ||
          message.includes('/rcm-api/activity-log'))
      ) {
        console.debug('[PlatformServices] Task Manager / Activity Log not reachable from browser');
        return;
      }
      originalConsoleError.apply(console, args);
    };

    const handleUnhandledRejection = (event) => {
      const errorMessage = event.reason?.message || '';
      const errorUrl = event.reason?.url || event.reason?.config?.url || '';
      
      // Suppress Stripe wallet-config 401 errors
      if (errorUrl.includes('merchant-ui-api.stripe.com/elements/wallet-config') || 
          errorMessage.includes('wallet-config')) {
        event.preventDefault();
        console.debug('[Stripe] Wallet features not available');
        return;
      }
      
      // Suppress Agentic Platform connection errors (service may not be running)
      if ((errorUrl.includes(':8076') || errorUrl.includes('agentic-platform')) &&
          (errorMessage.includes('ERR_CONNECTION_REFUSED') || 
           errorMessage.includes('Connection refused') ||
           errorMessage.includes('Failed to fetch'))) {
        event.preventDefault();
        console.debug('[Agentic Platform] Service not available (connection refused)');
        return;
      }

      if ((errorUrl.includes(':8051') || errorUrl.includes('/api/v1/workqueue')) &&
          (errorMessage.includes('Network Error') ||
           errorMessage.includes('ERR_CONNECTION_REFUSED') ||
           errorMessage.includes('Connection refused'))) {
        event.preventDefault();
        console.debug('[WorkQueue] Task Manager not available (connection refused)');
        return;
      }
      
      // Suppress authentication-related errors in production
      if (process.env.NODE_ENV === 'production') {
        if (errorMessage.includes('Invalid email or password') || 
            errorMessage.includes('Login failed') ||
            errorMessage.includes('Unauthorized')) {
          event.preventDefault();
          return;
        }
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      console.error = originalConsoleError; // Restore original console.error
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Service ports mapping
  const SERVICE_PORTS = {
    DENIAL_MANAGEMENT: 3154,
    CLAIMS_PROCESSING: 3153,
    PRIOR_AUTHORIZATION: 3151,
    INSURANCE_VERIFICATION: 3150,
    CHARGE_CAPTURE: 3152,
    AR_POSTING: 3155,
    EXPLANATION_OF_BENEFITS: 3157,
    CONTRACT_MANAGEMENT: 3156,
    RCM_COMPLIANCE: 3158,
    EDI_PLATFORM: 3010,
    AI_AGENT_BUILDER: 3075,
    PATIENTS: 3100,
    PRACTITIONERS: 3101,
    CLAIMS_CORE: 3102,
    CLINICAL_NOTES: 3107,   // CoreServices FhirDocumentReferences (FHIR Document / Clinical Notes)
    ENCOUNTERS: 3106,
    PATIENT_ACCOUNT: 3110,  // PatientAccount service (Patient Billing)
    INTEGRATIONS_SERVICE: 3060, // Integrations/frontend (Vite)
    PRACTICE_DATA_MANAGER: 3120,
    DATA_IMPORTER: 3052,
    MEDICAL_CODES: 3103,
  };

  const navigationItems = [
    { id: 'dashboard', name: 'Home', icon: 'LayoutDashboard', path: '/', type: 'internal' },
    { id: 'work-queue', name: 'Work Queue', icon: 'Inbox', path: '/work-queue', type: 'internal' },
    { id: 'denials', name: 'Denials & Appeals', icon: 'AlertTriangle', path: '/denials', type: 'service', servicePort: SERVICE_PORTS.DENIAL_MANAGEMENT, serviceName: 'Denials & Appeals' },
    { id: 'claims', name: 'Claims Processing', icon: 'FileText', path: '/claims', type: 'service', servicePort: SERVICE_PORTS.CLAIMS_PROCESSING, serviceName: 'Claims Processing' },
    { id: 'charges', name: 'Charge Management', icon: 'DollarSign', path: '/charges', type: 'service', servicePort: SERVICE_PORTS.CHARGE_CAPTURE, serviceName: 'Charge Management' },
    { id: 'medical-codes', name: 'Medical Codes', icon: 'ClipboardList', path: '/medical-codes', type: 'service', servicePort: SERVICE_PORTS.MEDICAL_CODES, serviceName: 'Medical Codes' },
    { id: 'ar-posting', name: 'AR Posting', icon: 'CreditCard', path: '/ar-posting', type: 'service', servicePort: SERVICE_PORTS.AR_POSTING, serviceName: 'AR Posting' },
    { id: 'eob', name: 'Remittance Advice', icon: 'Receipt', path: '/eob', type: 'service', servicePort: SERVICE_PORTS.EXPLANATION_OF_BENEFITS, serviceName: 'Remittance Advice' },
    { id: 'insurance', name: 'Eligibility & Benefits', icon: 'Shield', path: '/insurance', type: 'service', servicePort: SERVICE_PORTS.INSURANCE_VERIFICATION, serviceName: 'Insurance Verification' },
    { id: 'prior-auth', name: 'Prior Auth', icon: 'CheckCircle', path: '/prior-auth', type: 'service', servicePort: SERVICE_PORTS.PRIOR_AUTHORIZATION, serviceName: 'Prior Authorization' },
    { id: 'clearinghouse', name: 'EDI Platform', icon: 'Globe', path: '/clearinghouse', type: 'service', servicePort: SERVICE_PORTS.EDI_PLATFORM, serviceName: 'EDI Platform' },
    { id: 'patient-billing', name: 'Patient Billing', icon: 'CreditCard', path: '/patient-billing', type: 'service', servicePort: SERVICE_PORTS.PATIENT_ACCOUNT, serviceName: 'Patient Billing' },
    { id: 'compliance', name: 'Compliance', icon: 'Shield', path: '/compliance', type: 'service', servicePort: SERVICE_PORTS.RCM_COMPLIANCE, serviceName: 'RCM Compliance' },
    { id: 'analytics', name: 'Analytics', icon: 'BarChart', path: '/analytics', type: 'internal' },
    { id: 'reporting', name: 'Reporting', icon: 'FileText', path: '/reporting', type: 'internal' },
    { id: 'agent-studio', name: 'Agent Studio', icon: 'FileQuestion', path: '/agent-studio', type: 'service', servicePort: SERVICE_PORTS.AI_AGENT_BUILDER, serviceName: 'AI Agent Builder' },
    { id: 'subscription', name: 'Subscription', icon: 'CreditCard', path: '/subscription', type: 'internal' },
    { id: 'team', name: 'Team', icon: 'Users', path: '/team', type: 'internal' },
    {
      id: 'integrations',
      name: 'Integrations',
      icon: 'Globe',
      path: '/integrations',
      type: 'service',
      servicePort: SERVICE_PORTS.INTEGRATIONS_SERVICE,
      serviceName: 'Integrations Service',
    },
    {
      id: 'practice-data',
      name: 'Practice Data',
      icon: 'ClipboardList',
      path: '/practice-data',
      type: 'service',
      servicePort: SERVICE_PORTS.PRACTICE_DATA_MANAGER,
      serviceName: 'Practice Data',
    },
    { id: 'activity', name: 'Activity Log', icon: 'History', path: '/activity', type: 'internal' },
    { id: 'contract-management', name: 'Contract Management', icon: 'FileText', path: '/contract-management', type: 'service', servicePort: SERVICE_PORTS.CONTRACT_MANAGEMENT, serviceName: 'Contract Management' },
    { id: 'patient-portal', name: 'Patient Portal', icon: 'Key', path: '/patient-portal', type: 'service', servicePort: SERVICE_PORTS.PATIENTS, serviceName: 'Patient Portal Management' },
    { id: 'settings', name: 'Settings', icon: 'Settings', path: '/settings', type: 'internal' },
    // Support section
    { id: 'contact-us', name: 'Contact Us', icon: 'MessageCircle', path: '/contact-us', type: 'internal' },
  ];

  const appTree = (
            <QueryClientProvider client={queryClient}>
              <Router>
              <OrganizationProvider>
              <EffectiveAccessProvider>
              <NotificationProvider>
              <Routes>
                {/* Public Routes */}
                <Route 
                  path="/login" 
                  element={
                    <PublicRoute>
                      <LoginForm />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/register" 
                  element={
                    <PublicRoute>
                      <RegisterForm />
                    </PublicRoute>
                  } 
                />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify" element={<EmailVerifyPage appLabel="AgenticPractice" sourceApp="agentic_rcm" />} />
                <Route path="/invite" element={<InvitationAcceptPage />} />
                <Route
                  path="/legal/baa-accept"
                  element={
                    <ProtectedRoute>
                      <BaaAcceptPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <OnboardingPage />
                    </ProtectedRoute>
                  }
                />

                {/* Post-login home — explicit routes rank above the splat layout */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Navigate to={DEFAULT_HOME_ROUTE} replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Navigate to={DEFAULT_HOME_ROUTE} replace />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <ProtectedAppLayout navigationItems={navigationItems} servicePorts={SERVICE_PORTS} />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </NotificationProvider>
              </EffectiveAccessProvider>
              </OrganizationProvider>
            </Router>
          </QueryClientProvider>
  );

  return (
    <AuthProvider>
          {stripePromise ? <Elements stripe={stripePromise}>{appTree}</Elements> : appTree}
          <CrossAppNavHost />
    </AuthProvider>
  );
}

export default App;

